import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DigestSettings from "../components/DigestSettings";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import InstallHint from "../components/InstallHint";
import RestaurantEditor from "../components/RestaurantEditor";

export default function Settings() {
  const { user, organisation, restaurants, refresh, updateUser, can } = useAuth();
  const isOwner = can("manage_settings");
  const [orgName, setOrgName] = useState(organisation?.name || "");
  const [newRest, setNewRest] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const autoCapture = localStorage.getItem("supplivo_auto_capture") ?? String(user?.preferences?.auto_capture ?? true);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated"); setPw({ current_password: "", new_password: "", confirm: "" }); refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const saveOrg = async (e) => {
    e.preventDefault();
    try { await api.put("/organisation", { name: orgName }); toast.success("Organisation updated"); refresh(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const addRest = async (e) => {
    e.preventDefault();
    try { await api.post("/restaurants", { name: newRest }); toast.success("Restaurant added"); setNewRest(""); refresh(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const toggleAuto = async (val) => {
    localStorage.setItem("supplivo_auto_capture", String(val));
    try { const { data } = await api.put("/settings/preferences", { auto_capture: val }); updateUser(data); toast.success(`Auto Capture default ${val ? "ON" : "OFF"}`); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="settings-page" className="max-w-2xl">
      <PageHeader title="Settings" subtitle={isOwner ? "Organisation, restaurants and scanner preferences." : "Your account and scanner preferences."} />
      <div className="mb-4"><InstallHint /></div>
      <DigestSettings />
      {user?.is_superadmin && <Link to="/admin" className="lg:hidden block mb-4 text-sm bg-slate-900 text-white rounded-md px-4 py-2.5 font-medium" data-testid="settings-admin-link">Admin · AI usage & costs →</Link>}
      {user?.auth_provider === "password" && (
        <section className={`bg-white border rounded-lg p-5 mb-4 ${user?.must_change_password ? "border-amber-400" : "border-slate-200"}`} data-testid="password-section">
          <h2 className="font-semibold text-slate-900 mb-1">Password</h2>
          {user?.must_change_password && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="must-change-password-note">You're using a temporary password. Please choose your own now.</p>}
          <form onSubmit={changePassword} className="grid sm:grid-cols-3 gap-2">
            <Input type="password" required placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" className="h-11" />
            <Input type="password" required minLength={8} placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" className="h-11" />
            <Input type="password" required minLength={8} placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="confirm-password-input" className="h-11" />
            <Button type="submit" className="bg-slate-900 h-11 sm:col-span-3" data-testid="change-password-button">Update password</Button>
          </form>
        </section>
      )}
      {isOwner && (<>
      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
        <h2 className="font-semibold text-slate-900 mb-3">Organisation</h2>
        <form onSubmit={saveOrg} className="flex gap-2">
          <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="org-name-input" />
          <Button type="submit" className="bg-slate-900" data-testid="org-save-button">Save</Button>
        </form>
        <p className="text-xs text-slate-500 mt-2">Signed in as {user?.email} ({user?.auth_provider === "google" ? "Google" : "email"})</p>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
        <h2 className="font-semibold text-slate-900 mb-3">Restaurants</h2>
        <ul className="divide-y divide-slate-100 mb-3" data-testid="restaurant-list">
          {restaurants.map((r) => <RestaurantEditor key={r.id} restaurant={r} onSaved={refresh} />)}
        </ul>
        <form onSubmit={addRest} className="flex gap-2">
          <Input value={newRest} onChange={(e) => setNewRest(e.target.value)} placeholder="Add another restaurant" required data-testid="new-restaurant-input" />
          <Button type="submit" variant="outline" data-testid="add-restaurant-button">Add</Button>
        </form>
      </section>
      </>)}

      <section className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="font-semibold text-slate-900 mb-3">Scanner</h2>
        <div className="flex items-center justify-between">
          <div><Label>Auto Capture default</Label><p className="text-xs text-slate-500">Automatically photograph invoices when steady and in frame.</p></div>
          <Switch checked={autoCapture === "true"} onCheckedChange={toggleAuto} data-testid="auto-capture-default-switch" />
        </div>
      </section>
    </div>
  );
}
